/**
 * Ant Design Admin Theme Configuration
 * Uses default Ant Design blue theme (#1677ff)
 * Replaces Material UI adminTheme.ts
 */

import type { ThemeConfig } from 'antd';

// Color palette for status indicators (preserved from business logic)
export const colors = {
  gray: {
    50: '#f9fafb',
    100: '#f3f4f6',
    200: '#e5e7eb',
    300: '#d1d5db',
    400: '#9ca3af',
    500: '#6b7280',
    600: '#4b5563',
    700: '#374151',
    800: '#1f2937',
    900: '#111827',
  },
  success: {
    light: '#dcfce7',
    main: '#16a34a',
    dark: '#15803d',
  },
  warning: {
    light: '#fef9c3',
    main: '#ca8a04',
    dark: '#a16207',
  },
  error: {
    light: '#fee2e2',
    main: '#dc2626',
    dark: '#b91c1c',
  },
  info: {
    light: '#dbeafe',
    main: '#2563eb',
    dark: '#1d4ed8',
  },
};

// Role configuration colors (preserved exactly from business logic)
export const ROLE_CONFIG: Record<string, { color: string; description: string }> = {
  super_admin: { color: '#7c3aed', description: 'Full system access with all permissions' },
  admin: { color: '#2563eb', description: 'Administrative access with most permissions' },
  approver: { color: '#059669', description: 'Can approve/reject client registrations' },
  analyst: { color: '#d97706', description: 'Read-only access to analytics and reports' },
  tenant_admin: { color: '#dc2626', description: 'Manages specific tenant accounts' },
};

// Client user role configuration (preserved exactly from business logic)
export const CLIENT_USER_ROLE_CONFIG: Record<string, { color: string; description: string }> = {
  owner: { color: '#7c3aed', description: 'Full access to client account' },
  admin: { color: '#2563eb', description: 'Manage users and settings' },
  member: { color: '#059669', description: 'Standard API access' },
};

// Ant Design theme configuration
const antdAdminTheme: ThemeConfig = {
  token: {
    // Use default Ant Design blue primary color
    colorPrimary: '#1677ff',

    // Border radius for components
    borderRadius: 8,

    // Font settings
    fontFamily: '"SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',

    // Link color
    colorLink: '#1677ff',
    colorLinkHover: '#4096ff',

    // Success, warning, error colors
    colorSuccess: colors.success.main,
    colorWarning: colors.warning.main,
    colorError: colors.error.main,
    colorInfo: colors.info.main,

    // Background colors
    colorBgContainer: '#ffffff',
    colorBgLayout: '#f5f5f7', // Apple-inspired background

    // Text colors
    colorText: colors.gray[900],
    colorTextSecondary: colors.gray[500],
    colorTextDisabled: colors.gray[400],

    // Border colors
    colorBorder: colors.gray[200],
    colorBorderSecondary: colors.gray[100],
  },
  components: {
    Button: {
      borderRadius: 8,
      controlHeight: 40,
      controlHeightSM: 32,
      controlHeightLG: 48,
    },
    Input: {
      borderRadius: 8,
      controlHeight: 40,
    },
    Select: {
      borderRadius: 8,
      controlHeight: 40,
    },
    Card: {
      borderRadiusLG: 12,
    },
    Modal: {
      borderRadiusLG: 16,
    },
    Table: {
      borderRadiusLG: 12,
      headerBg: colors.gray[50],
      headerColor: colors.gray[700],
      rowHoverBg: colors.gray[50],
    },
    Tabs: {
      itemSelectedColor: '#1677ff',
      inkBarColor: '#1677ff',
    },
    Menu: {
      itemSelectedBg: 'rgba(22, 119, 255, 0.1)',
      itemSelectedColor: '#1677ff',
    },
    Tag: {
      borderRadiusSM: 6,
    },
    Alert: {
      borderRadiusLG: 8,
    },
    Drawer: {
      borderRadiusLG: 0,
    },
    Tooltip: {
      borderRadius: 6,
      colorBgSpotlight: colors.gray[900],
    },
    Progress: {
      circleTextFontSize: '1rem',
    },
  },
};

// Status color helper functions (preserved exactly from business logic)
export const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
  switch (status?.toLowerCase()) {
    case 'approved':
    case 'active':
      return 'success';
    case 'pending':
      return 'warning';
    case 'rejected':
    case 'inactive':
    case 'revoked':
      return 'error';
    default:
      return 'default';
  }
};

export const getHttpStatusColor = (statusCode: number): 'success' | 'warning' | 'error' => {
  if (statusCode >= 500) return 'error';
  if (statusCode >= 400) return 'warning';
  return 'success';
};

export const getLogLevelColor = (level: string): 'error' | 'warning' | 'processing' | 'default' => {
  const lowerLevel = level?.toLowerCase() || '';
  if (lowerLevel.includes('error')) return 'error';
  if (lowerLevel.includes('warn')) return 'warning';
  if (lowerLevel.includes('info')) return 'processing';
  return 'default';
};

export const getHealthColor = (health: number): 'success' | 'warning' | 'error' => {
  if (health >= 80) return 'success';
  if (health >= 50) return 'warning';
  return 'error';
};

export const getErrorRateColor = (errorRate: number): 'error' | 'warning' | 'default' => {
  if (errorRate > 5) return 'error';
  if (errorRate > 2) return 'warning';
  return 'default';
};

// Ant Design Tag color mapping for status
export const getTagColor = (status: string): string => {
  switch (status?.toLowerCase()) {
    case 'approved':
    case 'active':
    case 'completed':
    case 'success':
      return 'green';
    case 'pending':
    case 'processing':
      return 'orange';
    case 'rejected':
    case 'inactive':
    case 'revoked':
    case 'failed':
    case 'error':
      return 'red';
    default:
      return 'default';
  }
};

export default antdAdminTheme;
